/**
 * Helpers for the POST /v2/files/upload response (FS-555).
 *
 * Kept in its own module (not in AddDataModal.tsx) so it can be unit-tested and
 * imported without tripping react-refresh's component-only-export rule.
 */

/** Shape of the POST /v2/files/upload success body we rely on. */
export interface UploadResponse {
  /** The created/updated `documents` row id (server uses the file-upload id). */
  id: string
  /**
   * The name the row actually ENDED UP with, which is not always the one that
   * was sent: with `auto_rename` on (the default) the server resolves a
   * collision by storing `report (1).pdf` for a second `report.pdf`. Callers
   * that display a name must prefer this over the local `File.name`, or they
   * label a catalog row with a name it does not have.
   *
   * Optional because it is read defensively — an older body, or one shaped
   * unexpectedly, still yields a usable `id`.
   */
  originalFilename?: string
}

/**
 * Parse the upload response body and extract the server document id. Tolerant
 * of unexpected shapes — returns undefined rather than throwing so a parse miss
 * just skips the optimistic insert (the post-upload refetch still runs).
 */
export function parseUploadResponse(responseText: string): UploadResponse | undefined {
  try {
    const data = JSON.parse(responseText) as unknown
    const id = (data as { id?: unknown })?.id
    // Require a non-empty string id — a blank id would produce an optimistic row
    // that can't be deduped/SSE-matched against the real server row.
    if (typeof id === 'string' && id.length > 0) {
      // `file` is the FileStatusResponseDto the upload route returns beside the
      // id; its `originalFilename` is the post-auto-rename name.
      const name = (data as { file?: { originalFilename?: unknown } })?.file?.originalFilename
      return typeof name === 'string' && name.length > 0
        ? { id, originalFilename: name }
        : { id }
    }
  } catch {
    /* non-JSON / empty body — fall through */
  }
  return undefined
}
